// Error boundary for the PUBLIC livestream viewer (/live) — the screen a
// patient sees if the page throws mid-stream, and the only thing that reports
// that it happened.
//
// WHY IT EXISTS AT ALL (A6, 2026-09-04). /live had no `errorElement`, so a
// render-time throw fell through to React Router's stock white "Unexpected
// Application Error!" page: no brand, no wording a viewer can act on, and no
// way back into the stream except editing the URL — mid-broadcast, to a
// patient. The five admin livestream routes have had AdminRouteError for
// exactly this reason since PR #1978; the public route never got one.
//
// AND WHY IT REPORTS. A /live crash was, until this file, reported to NOBODY.
// RouterProvider wraps every route element in its own internal error boundary,
// so the throw is consumed there: it never reaches the root <ErrorBoundary> in
// index.tsx (the only thing that calls captureClientError) and never reaches
// window.onerror (an error boundary already handled it). That is why two passes
// over the owner's "React #185 when the window crosses 1024px" report produced
// no stack to work from. In a production build the message is the useless
// "Minified React error #185"; the COMPONENT STACK is the payload that names
// the looping component — and React only ever hands that over in
// componentDidCatch, which is why LiveRouteErrorBoundary below is a class and
// wraps the route ELEMENT rather than replacing it.
//
// NOT AdminRouteError. Same shape (useRouteError reduced to one screen, Reload
// = window.location.reload() on purpose — re-rendering the tree that just threw
// usually throws again), but for a viewer rather than an operator: viewer
// wording, and the technical message goes to telemetry ONLY. A patient is never
// shown a stack trace of our software.
//
// INLINE STYLES, NO TAILWIND, NO ./livestream.css — deliberately, twice over.
// (1) index.tsx imports this module EAGERLY, because an errorElement cannot be
// lazy: the chunk that failed to load may be the very reason we are rendering.
// Importing livestream.css from an eager module would drag the whole /live
// stylesheet out of its lazy chunk and into the funnel's main bundle. (2) A
// boundary has to be able to render with nothing on the page but itself. Same
// reasoning as LiveStreamLoadingShell in pages/LiveStreamPage.tsx — but NOT
// the same palette: that shell is antd grey (#f5f5f5 / #666), while this
// screen wears the slate the RUNNING viewer wears (LiveStreamApp's
// `bg-slate-100 text-slate-900` — #f1f5f9 / #0f172a, with #64748b for the
// secondary line), so a crash lands on the ground the stream was already on.
//
// SELF-CONTAINED for the same reason: no Relay hooks, no LiveReactionsProvider,
// no useAppConfig (that hook THROWS when its provider is missing — the brand
// mark is read straight off the context with a null check instead). The
// boundary must not be able to throw from the layer that threw.
//
// ONE ERROR IS NOT SHOWN THIS SCREEN AT ALL: a stale-bundle ChunkLoadError.
// /live is a link we send patients by SMS around a stream, so it is exactly the
// tab most likely to be holding a bundle a deploy has just replaced — and the
// answer to that is the same self-heal the rest of the app already performs, not
// a Reload button a viewer has to find mid-broadcast. See handleLiveRouteError.
import { Component, ErrorInfo, ReactNode, useContext, useEffect } from "react";
import { useRouteError } from "react-router-dom";
import { darkButtonStyle } from "./darkButtonStyle";
import { AppConfigContext } from "../providers/AppConfigProvider";
import {
  captureClientError,
  maybeReloadOnChunkError,
} from "../../utils/clientEvents";

/** Past this it is not a component stack any more, it is a transcript. */
const MAX_COMPONENT_STACK_CHARS = 4000;

/** React Router hands `unknown`; the chunk-error test needs a string. */
function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  const maybeMessage = (error as { message?: unknown } | null)?.message;
  return typeof maybeMessage === "string" ? maybeMessage : "";
}

/**
 * Everything both entry points do with a crash, in the order the root
 * <ErrorBoundary> does it (ErrorBoundary.tsx:23-35): report it, then let the
 * shared chunk-error handler decide whether this is a stale tab.
 *
 * `source`/`route` name the surface so a /live crash is separable from the root
 * boundary's rows; `componentStack` is the reason this boundary exists at all —
 * see the header.
 *
 * THE CHUNK CASE. A hashed chunk that 404s after a deploy is thrown during
 * render and caught HERE, so it reaches neither window.onerror nor the root
 * boundary — and /live is a link we hand patients by SMS around a stream, i.e.
 * exactly the tab most likely to be holding a bundle we have just replaced.
 * maybeReloadOnChunkError is the app's one implementation of that self-heal,
 * reload-loop guard included (10s debounce, hard cap of two attempts per tab,
 * counter reset after 5 min), and it beacons the buffered events out before it
 * reloads — which is why the capture above has to come first. No-op for every
 * other render error, which is what leaves the manual Reload screen below as
 * the fallback for all of them.
 */
function handleLiveRouteError(
  error: unknown,
  componentStack: string | null,
): void {
  try {
    captureClientError(error, {
      source: "route_error_boundary",
      route: "/live",
      componentStack: componentStack
        ? componentStack.slice(0, MAX_COMPONENT_STACK_CHARS)
        : null,
    });
  } catch {
    // Reporting must never be the SECOND thing that throws on this screen —
    // and it must never cost the viewer the self-heal below either.
  }
  try {
    maybeReloadOnChunkError(error, messageOf(error));
  } catch {
    // Same contract: a failed self-heal degrades to the Reload button.
  }
}

/**
 * What the viewer sees. Takes no props on purpose: there is nothing about the
 * error that belongs on a patient's screen, so there is nothing to pass.
 */
const LiveStreamErrorScreen = () => {
  // Straight off the context, not through useAppConfig — see the header. Null
  // when the provider is missing (it sits above the router, so in practice it
  // is there), and then the mark is simply omitted.
  const brandMarkSrc = useContext(AppConfigContext)?.brandMarkSrc ?? null;

  return (
    <div
      className="livestream-root"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        textAlign: "center",
        backgroundColor: "#f1f5f9", // slate-100, the LIVE shell's own ground
        color: "#0f172a", // slate-900
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      {brandMarkSrc && (
        // alt="" — the mark carries no information the copy below does not.
        // The brightness filter is what .live-brand-mark applies in
        // livestream.css, inlined because that stylesheet may not be loaded.
        <img
          src={brandMarkSrc}
          alt=""
          aria-hidden="true"
          style={{ height: 28, width: "auto", filter: "brightness(0.17)" }}
        />
      )}
      <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
        The stream page hit a problem
      </h1>
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: "#64748b" }}>
        Reload to rejoin the stream.
      </p>
      <button
        type="button"
        // A full reload, not a re-render: whatever state produced the throw is
        // still in memory, so re-mounting the same tree usually throws again.
        onClick={() => window.location.reload()}
        style={{
          appearance: "none",
          marginTop: 4,
          padding: "10px 24px",
          borderRadius: 9999,
          borderWidth: 1,
          borderStyle: "solid",
          fontSize: 14,
          fontWeight: 600,
          fontFamily: "inherit",
          cursor: "pointer",
          // Last so the slate-900 pill's background/border/text colors win —
          // the same shared style JoinCard's and ChatPanel's buttons use.
          ...darkButtonStyle(false),
        }}
      >
        Reload
      </button>
    </div>
  );
};

/**
 * Wraps the /live route ELEMENT. This is the path that actually catches a
 * viewer crash — and the only one that can hand React's component stack to
 * telemetry, because `useRouteError()` in an errorElement gives the error and
 * nothing else.
 *
 * It also covers the PublicCatchAllDispatcher copy of the viewer, which is
 * matched by the `/*` route and so has no errorElement of its own to fall back
 * on (see docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md — every /live entry
 * point is registered twice by design).
 */
export class LiveRouteErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    handleLiveRouteError(error, info.componentStack ?? null);
  }

  render(): ReactNode {
    if (this.state.failed) return <LiveStreamErrorScreen />;
    return this.props.children;
  }
}

/**
 * The route's `errorElement` — the backstop for everything the boundary above
 * cannot see: a router-level failure, or a throw from outside the element it
 * wraps. No component stack on this path (React Router's own boundary caught
 * it first), but the error and the route still get reported.
 */
const LiveRouteError = () => {
  const error = useRouteError();

  // From an effect, not from render: an errorElement can re-render, and does
  // render twice under StrictMode — one crash has to produce one row.
  useEffect(() => {
    handleLiveRouteError(error, null);
  }, [error]);

  return <LiveStreamErrorScreen />;
};

export default LiveRouteError;
